import common from "./common";
import definitions from "./definitions";
import SaveData from "./game/SaveData";
import BossFightScene from "./scenes/BossFightScene";
import GameScene from "./scenes/GameScene";
import InventoryScene from "./scenes/InventoryScene";
import MapScene from "./scenes/MapScene";
import MinigameResultScene from "./scenes/MinigameResultScene";
import OutroScene from "./scenes/OutroScene";
import PauseScene from "./scenes/PauseScene";
import QuestScene from "./scenes/QuestScene";
import SettingsScene from "./scenes/SettingsScene";
import ShopScene from "./scenes/ShopScene";
import SlotsScene from "./scenes/SlotsScene";
import SplashScene from "./scenes/SplashScene";
import settings from "./settings";

export default class Application {
    constructor() {

        const saveData = new SaveData();
        common.saveData = saveData;

        saveData.createEmptySlot(0);
        saveData.setActiveSlot(0);

        P3.audio.mute(saveData.settings.muted);

        if (settings.APP) {
            common.config.language = common.saveData.settings.language;
        }

        this.showSplashScene();
        //this.showOutroScene();

        //this.showBossFightScene(definitions.BOSS_ID.BATWITCH);

        // TODO DEBUG
        //definitions.DEFAULT_LEVEL_ID = definitions.LEVEL_ID.TUTORIAL;
        //this.showGameScene();

        //P3.audio.mute(true);
    }

    /**
     * @return {SplashScene}
     */
    showSplashScene() {
        const scene = new SplashScene();
        scene.signals.next.add(() => {
            if (settings.PHASE == 1) {
                if (common.saveData.activeSlot.player.tutorial && !settings.SKIP_TUTORIAL) {
                    definitions.DEFAULT_LEVEL_ID = definitions.LEVEL_ID.TUTORIAL;
                } else {
                    definitions.DEFAULT_LEVEL_ID = definitions.LEVEL_ID.FOREST_CLEARING;
                }
                this.showGameScene();
            } else {
                this.showSlotsScene();
            }
        });
        scene.signals.settings.add(() => {
            scene.pauseTweens();
            this.showSettingsScene(scene);
        });
        P3.scene.show(scene);
        SplashScene.active = scene;
        return scene;
    }

    /**
    * @return {PauseScene}
    */
    showSettingsScene(splashScene) {
        const scene = new SettingsScene();
        scene.signals.previous.once(() => {
            splashScene.resumeTweens();
            P3.scene.remove();
        });
        scene.signals.next.once(() => {
            this.showSplashScene();
        });
        P3.scene.add(scene);
        SettingsScene.active = scene;
        return scene;
    }

    /**
     * @return {SlotsScene}
     */
    showSlotsScene() {
        const scene = new SlotsScene();
        scene.signals.next.add(() => {
            if (common.saveData.activeSlot.player.tutorial && !settings.SKIP_TUTORIAL) {
                definitions.DEFAULT_LEVEL_ID = definitions.LEVEL_ID.TUTORIAL;
            } else {
                definitions.DEFAULT_LEVEL_ID = definitions.LEVEL_ID.FOREST_CLEARING;
            }
            this.showGameScene();
        });
        scene.signals.previous.once(() => {
            this.showSplashScene();
        });
        P3.scene.show(scene);
        SlotsScene.active = scene;
        return scene;
    }

    /**
     * @return {GameScene}
     */
    showGameScene() {
        const scene = new GameScene();
        scene.signals.pause.add(() => {
            scene.pauseTweens();

            const pause = this.showPauseScene();
            pause.signals.next.once(() => {
                scene.resumeTweens();
                scene.resume();
            });
        });
        scene.signals.map.add(() => {
            this.showMapScene(scene);
        });
        scene.signals.quest.add((questLog) => {
            this.showQuestScene(questLog, scene);
        });
        scene.signals.inventory.add((inventory) => {
            this.showInventoryScene(inventory, scene);
        });
        scene.signals.shop.add(() => {
            this.showShopScene(scene);
        });
        scene.signals.boss.add((boss) => {
            scene.pauseTweens();

            const fight = this.showBossFightScene(boss);
            fight.signals.next.once((success) => {
                scene.resumeTweens();
                scene.completeBossFight(success);
            });
        });
        scene.signals.minigame.add((minigame) => {
            scene.pauseTweens();

            const result = this.showMinigameResultScene(minigame);
            result.signals.next.once(() => {
                scene.resumeTweens();
            });
        });
        scene.signals.outro.add(() => {
            this.showOutroScene();
        });
        P3.scene.show(scene);
        GameScene.active = scene;
        return scene;
    }

    /**
     * @param {string} boss
     * @return {BossFightScene}
     */
    showBossFightScene(boss) {
        const scene = new BossFightScene(boss);
        scene.signals.pause.add(() => {
            scene.pauseTweens();

            const pause = this.showPauseScene();
            pause.signals.next.once(() => {
                scene.resumeTweens();
            });
        });
        scene.signals.next.once((victory) => {
            P3.scene.remove();
        });
        P3.scene.add(scene, true);
        BossFightScene.active = scene;
        return scene;
    }

    /**
     * @return {PauseScene}
     */
    showPauseScene() {
        const scene = new PauseScene();
        scene.signals.next.once(() => {
            P3.scene.remove();
        });
        scene.signals.previous.once(() => {
            this.showSplashScene();
        });
        P3.scene.add(scene);
        PauseScene.active = scene;
        return scene;
    }

    /**
     * @param {GameScene} [game=null]
     * @returns {MapScene}
     */
    showMapScene(game = null) {
        const scene = new MapScene();
        scene.signals.next.add((location) => {
            game && game.loadLevel(location).then();
            P3.scene.remove();
        });
        scene.signals.previous.add(() => {
            P3.scene.remove();
            if (game) {
                game.resume();
            }
        });
        P3.scene.add(scene);
        MapScene.active = scene;
        return scene;
    }

    /**
     * @param {QuestManager} questManager
     * @returns {QuestScene}
     */
    showQuestScene(questManager, game = null) {
        const scene = new QuestScene(questManager);
        scene.signals.previous.once(() => {
            P3.scene.remove();
            if (game) {
                game.resume();
            }
        });
        scene.signals.locationJump.once((level) => {
            P3.scene.remove();
            if (game) {
                game.resume();
                game.loadLevel(level);
            }
        })
        P3.scene.add(scene);
        QuestScene.active = scene;
        return scene;
    }

    /**
     * @param {Inventory} inventory
     * @returns {InventoryScene}
     */
    showInventoryScene(inventory, game = null) {
        const scene = new InventoryScene(inventory);
        scene.signals.previous.add(() => {
            P3.scene.remove();
            if (game) {
                game.resume();
            }
        });
        P3.scene.add(scene);
        InventoryScene.active = scene;
        return scene;
    }

        /**
     * @param {QuestManager} questManager
     * @returns {QuestScene}
     */
    showShopScene(game = null) {
        
        const scene = new ShopScene();
        scene.signals.previous.once(() => {
            P3.scene.remove();
            if (game) {
                game.resume();
            }
        });
        P3.scene.add(scene);
        ShopScene.active = scene;
        return scene;
    }

    /**
     * @param {Quest} minigame
     * @return {MinigameResultScene}
     */
    showMinigameResultScene(minigame) {
        const scene = new MinigameResultScene(minigame);
        scene.signals.next.once(() => {
            P3.scene.remove();
        });
        P3.scene.add(scene);
        MinigameResultScene.active = scene;
        return scene;
    }

    /**
     * @returns {OutroScene}
     */
    showOutroScene() {
        const scene = new OutroScene();
        scene.signals.next.add(() => {
            this.showSplashScene();
        });
        P3.scene.add(scene);
        return scene;
    }

    fadeTransition(a, b, scope) {
        scope = scope || window;

        const transition = new P3.FadeTransition(0x0);
        common.transitions.addChild(transition);

        transition.signals.in.once(function() {
            transition.out();
            a && a.call(scope);
        }, this);

        transition.signals.out.once(function() {
            transition.destroy();
            b && b.call(scope);
        }, this);

        transition.in();
    }
}